import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Flag, ExternalLink, ShieldCheck, ShieldX, Search, ClipboardList } from 'lucide-react';
import { logAdminAction } from '@/lib/auditLog';
import { Link } from 'react-router-dom';

type Report = {
  id: string;
  reporter_id: string | null;
  reporter_email: string | null;
  target_type: string;
  target_id: string;
  reason: string;
  details: string | null;
  status: 'open' | 'investigating' | 'resolved' | 'dismissed';
  resolution_notes: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
};

const statusConfig: Record<string, { label: string; color: string }> = {
  open: { label: 'Open', color: 'bg-yellow-100 text-yellow-800' },
  investigating: { label: 'Investigating', color: 'bg-blue-100 text-blue-800' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800' },
  dismissed: { label: 'Dismissed', color: 'bg-gray-200 text-gray-700' },
};

const targetLink = (t: string, id: string): string | null => {
  switch (t) {
    case 'property': return `/property/${id}`;
    case 'plot': return `/plot/${id}`;
    case 'project': return `/projects/${id}`;
    case 'agency': return `/agencies/${id}`;
    case 'agent': return `/agents/${id}`;
    default: return null;
  }
};

export function ReportsQueue() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'open' | 'investigating' | 'resolved' | 'dismissed' | 'all'>('open');
  const [selected, setSelected] = useState<Report | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const { data: reports, isLoading } = useQuery({
    queryKey: ['reports-queue'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reports' as any)
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as Report[];
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: Report['status']; notes?: string }) => {
      const update: any = { status };
      if (status === 'resolved' || status === 'dismissed') {
        update.resolution_notes = notes ?? null;
        update.resolved_by = user?.id ?? null;
        update.resolved_at = new Date().toISOString();
      }
      const { error } = await supabase.from('reports' as any).update(update).eq('id', id);
      if (error) throw error;

      await logAdminAction({
        action: status === 'resolved' ? 'report_resolve' : status === 'dismissed' ? 'report_dismiss' : 'report_investigate',
        entity_type: 'report',
        entity_id: id,
        reason: notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports-queue'] });
      toast.success('Report updated');
      setSelected(null);
      setResolutionNotes('');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const filtered = (reports || []).filter(r => tab === 'all' ? true : r.status === tab);
  const counts = {
    open: reports?.filter(r => r.status === 'open').length || 0,
    investigating: reports?.filter(r => r.status === 'investigating').length || 0,
    resolved: reports?.filter(r => r.status === 'resolved').length || 0,
    dismissed: reports?.filter(r => r.status === 'dismissed').length || 0,
  };

  if (isLoading) {
    return <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flag className="h-5 w-5 text-primary" />
          <h2 className="font-display text-xl font-bold">Reports & Abuse Queue</h2>
        </div>
        {counts.open > 0 && <Badge className="bg-yellow-500 text-white">{counts.open} Open</Badge>}
      </div>

      <Tabs value={tab} onValueChange={v => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="open">Open ({counts.open})</TabsTrigger>
          <TabsTrigger value="investigating">Investigating ({counts.investigating})</TabsTrigger>
          <TabsTrigger value="resolved">Resolved ({counts.resolved})</TabsTrigger>
          <TabsTrigger value="dismissed">Dismissed ({counts.dismissed})</TabsTrigger>
          <TabsTrigger value="all">All ({reports?.length || 0})</TabsTrigger>
        </TabsList>
      </Tabs>

      {!filtered.length ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No reports in this category.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => {
            const cfg = statusConfig[r.status];
            const link = targetLink(r.target_type, r.target_id);
            return (
              <Card key={r.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                    <Flag className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold capitalize">{r.target_type}</span>
                      <Badge variant="outline" className="capitalize">{r.reason.replace(/_/g, ' ')}</Badge>
                      <Badge className={cfg.color}>{cfg.label}</Badge>
                    </div>
                    {r.details && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{r.details}</p>}
                    <div className="text-xs text-muted-foreground mt-1">
                      Reported {new Date(r.created_at).toLocaleString()}
                      {r.reporter_email && <> · by {r.reporter_email}</>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {link && (
                      <Link to={link} target="_blank">
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="h-4 w-4 mr-1" /> View
                        </Button>
                      </Link>
                    )}
                    <Button variant="outline" size="sm" onClick={() => { setSelected(r); setResolutionNotes(r.resolution_notes || ''); }}>
                      <Search className="h-4 w-4 mr-1" /> Triage
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Triage Report</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-sm space-y-1.5 bg-muted/50 rounded-lg p-3">
                <div><strong>Target:</strong> <span className="capitalize">{selected.target_type}</span> · <code className="text-xs">{selected.target_id}</code></div>
                <div><strong>Reason:</strong> <span className="capitalize">{selected.reason.replace(/_/g, ' ')}</span></div>
                <div><strong>Status:</strong> {statusConfig[selected.status].label}</div>
                {selected.reporter_email && <div><strong>Reporter:</strong> {selected.reporter_email}</div>}
                {selected.details && <div className="pt-1"><strong>Details:</strong><br />{selected.details}</div>}
              </div>

              <div>
                <label className="text-sm font-medium">Resolution notes (optional)</label>
                <Textarea
                  value={resolutionNotes}
                  onChange={e => setResolutionNotes(e.target.value)}
                  placeholder="What action was taken?"
                  rows={3}
                />
              </div>

              <DialogFooter className="gap-2 flex-wrap">
                {selected.status !== 'investigating' && (
                  <Button
                    variant="outline"
                    onClick={() => updateStatus.mutate({ id: selected.id, status: 'investigating' })}
                    disabled={updateStatus.isPending}
                  >
                    <Search className="h-4 w-4 mr-1" /> Mark Investigating
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => updateStatus.mutate({ id: selected.id, status: 'dismissed', notes: resolutionNotes })}
                  disabled={updateStatus.isPending}
                >
                  <ShieldX className="h-4 w-4 mr-1" /> Dismiss
                </Button>
                <Button
                  onClick={() => updateStatus.mutate({ id: selected.id, status: 'resolved', notes: resolutionNotes })}
                  disabled={updateStatus.isPending}
                >
                  <ShieldCheck className="h-4 w-4 mr-1" /> Resolve
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
